// src/pages/AdminSubcategoriesPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminSubcategoriesPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminSubcategoriesPage() {
  const navigate = useNavigate();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSubcategory, setDeletingSubcategory] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    category_id: "",
    sort: "latest"
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [isSearching, setIsSearching] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    image: null
  });

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const getImageUrl = (subcategory) => {
    if (!subcategory?.image) return null;
    if (subcategory.image_url) return subcategory.image_url;
    return `${API_BASE.replace("/api", "")}/storage/${subcategory.image}`;
  };

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchData(token);
  }, [navigate]);

  const searchSubcategories = async (token, query = "", filters = {}, page = 1) => {
    setIsSearching(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      // Build search parameters
      const searchParams = new URLSearchParams();
      if (query.trim()) searchParams.append('q', query.trim());
      if (filters.category_id) searchParams.append('category_id', filters.category_id);
      if (filters.sort) searchParams.append('sort', filters.sort);
      searchParams.append('page', page);
      searchParams.append('per_page', pagination.per_page);

      const searchUrl = `${API_BASE}/subcategories?${searchParams.toString()}`;
      const subcategoriesRes = await fetch(searchUrl, { headers });

      if (subcategoriesRes.ok) {
        const searchData = await subcategoriesRes.json();
        if (searchData.success) {
          setSubcategories(searchData.data || []);
          setPagination({
            current_page: 1,
            per_page: 20,
            total: searchData.data?.length || 0,
            last_page: 1
          });
        } else {
          setSubcategories([]);
        }
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      toast.error("Error searching subcategories");
      console.error("Search error:", error);
      setSubcategories([]);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const [subcategoriesRes, categoriesRes] = await Promise.all([
        fetch(`${API_BASE}/subcategories`, { headers }),
        fetch(`${API_BASE}/categories`, { headers }),
      ]);

      if (subcategoriesRes.ok) {
        const subcategoriesData = await subcategoriesRes.json();
        if (subcategoriesData.success) {
          setSubcategories(subcategoriesData.data || []);
        } else {
          setSubcategories([]);
        }
      } else {
        setSubcategories([]);
      }

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        if (Array.isArray(categoriesData?.categories)) {
          setCategories(categoriesData.categories);
        } else if (Array.isArray(categoriesData?.data)) {
          setCategories(categoriesData.data);
        } else if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else {
          setCategories([]);
        }
      } else {
        setCategories([]);
      }
    } catch (error) {
      toast.error("Error fetching data");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  // Search handlers
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      const token = getToken();
      if (token) {
        searchSubcategories(token, value, searchFilters, 1);
      }
    }, 500);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...searchFilters, [filterName]: value };
    setSearchFilters(newFilters);
    
    const token = getToken();
    if (token) {
      searchSubcategories(token, searchQuery, newFilters, 1);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchFilters({
      category_id: "",
      sort: "latest"
    });
    const token = getToken();
    if (token) {
      fetchData(token);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error("Please enter subcategory name");
      return;
    }
    if (!formData.category_id) {
      toast.error("Please select a category");
      return;
    }

    // Build FormData
    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("category_id", formData.category_id);
    
    if (formData.image) {
      fd.append("image", formData.image);
    }

    // Decide URL & spoof method when editing
    const isEdit = Boolean(editingSubcategory);
    const url = isEdit
      ? `${API_BASE}/subcategories/${editingSubcategory.id}`
      : `${API_BASE}/subcategories`;

    if (isEdit) {
      fd.append("_method", "PUT"); // spoof PUT
    }

    try {
      const response = await fetch(url, {
        method: "POST", // always POST (PUT is spoofed above)
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          // DO NOT set Content-Type for FormData
        },
        body: fd,
      });

      if (response.ok) {
        toast.success(isEdit ? "Subcategory updated successfully" : "Subcategory created successfully");
        setShowModal(false);
        setEditingSubcategory(null);
        resetForm();
        fetchData(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          toast.error(firstMsg || errorData.message || "Failed to save subcategory");
        } else {
          toast.error(errorData.message || "Failed to save subcategory");
        }
        console.error("Save error detail:", errorData);
      }
    } catch (error) {
      toast.error("Error saving subcategory");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name ?? "",
      category_id: subcategory.category_id ?? "",
      image: null
    });
    setShowModal(true);
  };

  const handleDelete = async (subcategoryId) => {
    const subcategory = subcategories.find(s => s.id === subcategoryId);
    setDeletingSubcategory(subcategory);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingSubcategory) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const res = await fetch(`${API_BASE}/subcategories/${deletingSubcategory.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (res.ok) {
        toast.success("Subcategory deleted successfully");
        setShowDeleteModal(false);
        setDeletingSubcategory(null);
        fetchData(token);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete subcategory");
      }
    } catch (error) {
      toast.error("Error deleting subcategory");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingSubcategory(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category_id: "",
      image: null
    });
  };

  const openModal = () => {
    setEditingSubcategory(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSubcategory(null);
    resetForm();
  };

  const getCategoryName = (categoryId) => {
    if (!Array.isArray(categories)) return "Unknown";
    const category = categories.find((cat) => Number(cat.id) === Number(categoryId));
    return category ? category.name : "Unknown";
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-subcategories">
          <div className="loading">Loading subcategories...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-subcategories">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Subcategories Management</h1>
            <p>Manage your product subcategories</p>
          </div>
          <div className="admin-actions">
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Subcategory
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="search-filters-container">
          <div className="search-section">
            <div className="search-input-group">
              <div className="search-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search subcategories by name..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="search-input"
              />
              {searchQuery && (
                <button className="clear-search" onClick={clearSearch}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className="filters-row">
              <div className="filter-group">
                <label>Category</label>
                <select
                  value={searchFilters.category_id}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Sort By</label>
                <select
                  value={searchFilters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                >
                  <option value="latest">Latest</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                </select>
              </div>
            </div>
          </div>
          
          {isSearching && (
            <div className="search-loading">
              <Loader size={20} strokeWidth={2} />
              <span>Searching...</span>
            </div>
          )}
        </div>

        {/* Subcategories Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subcategories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-state">
                    No subcategories found
                  </td>
                </tr>
              ) : (
                subcategories.map((subcategory) => {
                  const imgUrl = getImageUrl(subcategory);
                  return (
                    <tr key={subcategory.id}>
                      <td>
                        <div className="subcategory-image">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={subcategory.name}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextSibling) {
                                  e.currentTarget.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="image-placeholder"
                            style={{ display: imgUrl ? "none" : "flex" }}
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="subcategory-name">
                          <strong>{subcategory.name}</strong>
                        </div>
                      </td>
                      <td>{getCategoryName(subcategory.category_id)}</td>
                      <td>
                        {new Date(subcategory.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(subcategory)}
                            title="Edit"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(subcategory.id)}
                            title="Delete"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="10"
                                y1="11"
                                x2="10"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="14"
                                y1="11"
                                x2="14"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSubcategory ? "Edit Subcategory" : "Add New Subcategory"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="name">Subcategory Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter subcategory name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category_id">Category</label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {Array.isArray(categories) &&
                      categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="image">Subcategory Image</label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                  {editingSubcategory && editingSubcategory.image && (
                    <div className="current-image">
                      <p>Current image:</p>
                      <img 
                        src={getImageUrl(editingSubcategory)} 
                        alt="Current subcategory" 
                        style={{ maxWidth: '100px', maxHeight: '100px' }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingSubcategory ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingSubcategory ? "Update Subcategory" : "Create Subcategory"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Subcategory"
          message="Are you sure you want to delete this subcategory?"
          itemName={deletingSubcategory ? deletingSubcategory.name : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
